import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandingProvider, useBranding } from "@/contexts/BrandingContext";
import SetupPage from "@/pages/SetupPage";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import ProfilePage from "@/pages/ProfilePage";
import DashboardPage from "@/pages/DashboardPage";
import MembersPage from "@/pages/MembersPage";
import QualificationsPage from "@/pages/QualificationsPage";
import CertificatesPage from "@/pages/CertificatesPage";
import CoursesPage from "@/pages/CoursesPage";
import StaffCalendarPage from "@/pages/StaffCalendarPage";
import EnquiriesPage from "@/pages/EnquiriesPage";
import BusinessesPage from "@/pages/BusinessesPage";
import BusinessDetailPage from "@/pages/BusinessDetailPage";
import TeachIndexPage from "@/pages/TeachIndexPage";
import TeachPage from "@/pages/TeachPage";
import TrainingMatrixPage from "@/pages/TrainingMatrixPage";
import PricingPage from "@/pages/PricingPage";
import PaymentsPage from "@/pages/PaymentsPage";
import ExpensesPage from "@/pages/ExpensesPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import EmailLogsPage from "@/pages/EmailLogsPage";
import SettingsPage from "@/pages/SettingsPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import ImportsPage from "@/pages/ImportsPage";
import ImportBatchPage from "@/pages/ImportBatchPage";
import HolidaysPage from "@/pages/HolidaysPage";
import T4RDashboardPage from "@/pages/t4r/T4RDashboardPage";
import T4RCoursesPage from "@/pages/t4r/T4RCoursesPage";
import T4RCourseDetailPage from "@/pages/t4r/T4RCourseDetailPage";
import T4RLearnersPage from "@/pages/t4r/T4RLearnersPage";
import T4RAttemptsPage from "@/pages/t4r/T4RAttemptsPage";
import { hasRetrofitAccess, isAdmin, isSuperAdmin } from "@/utils/roles";
import GovernancePage from "@/pages/GovernancePage";
import AgreementsPage from "@/pages/AgreementsPage";
import LearnerPortalPage from "@/pages/LearnerPortalPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-muted-foreground font-body">Loading...</div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Force first-login password change before anything else
  if (user?.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

function RetrofitGuard() {
  const { user } = useAuth();
  return hasRetrofitAccess(user) ? <Outlet /> : <Navigate to="/" replace />;
}

function RoleGuard({ min }) {
  const { user } = useAuth();
  const ok = min === "provider" ? !!user?.is_provider : min === "superadmin" ? isSuperAdmin(user?.role) : isAdmin(user?.role);
  return ok ? <Outlet /> : <Navigate to="/" replace />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const { loaded, setup_complete } = useBranding();
  if (loaded && !setup_complete) return (
    <Routes>
      <Route path="/setup" element={<SetupPage />} />
      <Route path="*" element={<Navigate to="/setup" replace />} />
    </Routes>
  );
  if (loading || !loaded) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-muted-foreground font-body">Loading...</div>
    </div>
  );
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/setup" element={<Navigate to="/login" replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      {/* Retrofit learner portal — public, totally separate from the CRM shell + auth */}
      <Route path="/learn" element={<LearnerPortalPage />} />
      <Route path="/learn/accept" element={<LearnerPortalPage />} />
      <Route path="/change-password" element={<ProtectedRoute><div className="min-h-screen bg-background p-6"><ChangePasswordPage /></div></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="members" element={<MembersPage />} />
        <Route path="businesses" element={<BusinessesPage />} />
        <Route path="businesses/:id" element={<BusinessDetailPage />} />
        <Route path="teach" element={<TeachIndexPage />} />
        <Route path="teach/:qualId" element={<TeachPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="qualifications" element={<QualificationsPage />} />
        <Route path="certificates" element={<CertificatesPage />} />
        <Route path="courses" element={<CoursesPage />} />
        <Route path="staff-calendar" element={<StaffCalendarPage />} />
        <Route path="enquiries" element={<EnquiriesPage />} />
        <Route path="payment-success" element={<PaymentSuccessPage />} />
        <Route path="holidays" element={<HolidaysPage />} />
        <Route element={<RoleGuard min="admin" />}>
          <Route path="training-matrix" element={<TrainingMatrixPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="expenses" element={<ExpensesPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="admin" element={<SettingsPage />} />
        </Route>
        <Route element={<RoleGuard min="provider" />}>
          <Route path="governance" element={<GovernancePage />} />
        </Route>
        <Route element={<RoleGuard min="superadmin" />}>
          <Route path="agreements" element={<AgreementsPage />} />
          <Route path="email-logs" element={<EmailLogsPage />} />
          <Route path="imports" element={<ImportsPage />} />
          <Route path="imports/:id" element={<ImportBatchPage />} />
        </Route>
        <Route path="lms" element={<Navigate to="/t4r" replace />} />
        <Route path="t4r" element={<RetrofitGuard />}>
          <Route index element={<T4RDashboardPage />} />
          <Route path="courses" element={<T4RCoursesPage />} />
          <Route path="courses/:courseId" element={<T4RCourseDetailPage />} />
          <Route path="learners" element={<T4RLearnersPage />} />
          <Route path="attempts" element={<T4RAttemptsPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <BrandingProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster richColors position="top-right" visibleToasts={3} closeButton />
        </AuthProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}

export default App;
